import { useTranslation } from "@next-platform/client-common-core";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CharacterCardVariant,
  Chip,
  ChipSize,
  HorizontalListItem,
  Shape,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Trash } from "lucide-react";

import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";

import { CharacterCardData, CharacterItemAction } from "../../types";

interface HorizontalSelectableCardProps {
  characterData: CharacterCardData;
  showVisibilityBadge?: boolean;
  isSelected: boolean;
  onToggle: (action: CharacterItemAction) => void;
  isDisabled: boolean;
  onLimitReached?: () => void;
}

const visibilityChipClassNames = { text: "line-clamp-1" };

const HorizontalSelectableCard = ({
  characterData,
  showVisibilityBadge = false,
  isSelected,
  onToggle,
  isDisabled,
  onLimitReached,
}: HorizontalSelectableCardProps) => {
  const { t } = useTranslation();
  const { characterName, characterCreatorUserName, characterTitle, characterAvatarUrl, visibilityBadgeLabel, characterCardVariant } = characterData;
  const avatarUrl = getAvatarUrl(characterAvatarUrl || "", { preset: AvatarPreset.AVATAR_48X48 }) ?? undefined;

  const isButtonDisabled = !isSelected && isDisabled;
  const actionLabel = isSelected ? "X" : t("common:add");

  const handleButtonContainerClick = () => {
    if (isButtonDisabled && !isSelected && onLimitReached) {
      onLimitReached();
    }
  };

  const handleButtonClick = () => {
    onToggle(isSelected ? CharacterItemAction.REMOVE : CharacterItemAction.ADD);
  };

  const shouldShowVisibilityChip = [CharacterCardVariant.PUBLIC, CharacterCardVariant.PRIVATE, CharacterCardVariant.UNLISTED].includes(
    characterCardVariant,
  );

  const visibilityChipClassName = characterCardVariant === CharacterCardVariant.PUBLIC ? "!bg-green-5" : "";

  const renderAction = () => (
    <div className="shrink-0 ml-auto" onClick={handleButtonContainerClick}>
      <Button
        variant={isSelected ? ButtonVariant.OUTLINED : ButtonVariant.SECONDARY}
        size={ButtonSize.MEDIUM}
        onClick={handleButtonClick}
        disabled={isButtonDisabled}
        className={cn("flex items-center justify-center w-[60px]", isButtonDisabled ? "text-oled bg-oledContrast pointer-events-none" : "")}
      >
        {isSelected ? (
          <span className="flex items-center justify-center">
            <Trash className="w-4 h-4 text-oledContrast" />
          </span>
        ) : (
          <span className="flex items-center justify-center">
            <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-oledContrast mr-1">
              {actionLabel}
            </Text>
          </span>
        )}
      </Button>
    </div>
  );

  const titleElement = (
    <div className="flex flex-col gap-1 min-w-0">
      <Text variant={TextVariants.LABEL_LARGE_BOLD} className="text-text1">
        {characterName}
        {characterCreatorUserName ? (
          <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR} className="font-normal text-xs text-blue-9 ml-2">
            @{characterCreatorUserName}
          </Text>
        ) : null}
      </Text>

      {showVisibilityBadge && visibilityBadgeLabel && shouldShowVisibilityChip ? (
        <Chip
          shape={Shape.SQUARE}
          size={ChipSize.XSMALL}
          className={cn("w-fit cursor-default", visibilityChipClassName)}
          classNames={visibilityChipClassNames}
        >
          {visibilityBadgeLabel}
        </Chip>
      ) : null}
    </div>
  );

  return (
    <div className="w-full sm:max-w-[calc(50%-9px)] sm:basis-[calc(50%-9px)]">
      <HorizontalListItem avatar={avatarUrl} title={titleElement} description={characterTitle} action={renderAction()} />
    </div>
  );
};

export default HorizontalSelectableCard;
